import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Clock, QrCode, AlertTriangle, Users, MapPin, RefreshCw, Loader2,
  ChevronRight, CheckCircle, Monitor, Calendar
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import MobileLayout from '../components/layout/MobileLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [attendance, setAttendance] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clockingIn, setClockingIn] = useState(false);
  const [showShiftModal, setShowShiftModal] = useState(false);
  const [availableShifts, setAvailableShifts] = useState([]);
  const [pendingLocation, setPendingLocation] = useState({});

  const isSupervisor = ['supervisor', 'admin', 'super_admin'].includes(user?.role);

  const loadData = useCallback(async () => {
    try {
      console.log('[Dashboard] Loading data...');
      const [statsData, scansData, attendanceData] = await Promise.all([
        api.getDashboardStats(),
        api.getTodayScans(),
        api.getAttendanceStatus(),
      ]);
      console.log('[Dashboard] Attendance status:', attendanceData);
      setStats(statsData);
      setRecentScans(scansData.slice(0, 5));
      setAttendance(attendanceData);
    } catch (error) {
      console.error('[Dashboard] Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const getLocation = async () => {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({});
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (position) => resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        }),
        () => resolve({}),
        { timeout: 5000, enableHighAccuracy: true }
      );
    });
  };

  const handleClockInOut = async () => {
    setClockingIn(true);
    try {
      const locationData = await getLocation();

      if (attendance?.clocked_in) {
        await api.clockOut(locationData);
        // Immediately update state to show Clock In button
        setAttendance(prev => ({ ...prev, clocked_in: false }));
        await loadData();
      } else {
        const shiftsResponse = await api.getMyShiftsToday();
        
        if (!shiftsResponse.has_shifts) {
          alert('No Shift Scheduled. Please contact your supervisor.');
          return;
        }
        
        const availableToClockIn = shiftsResponse.shifts.filter(s => !s.already_clocked);
        
        if (availableToClockIn.length === 0) {
          alert('You have already clocked in for all your scheduled shifts today.');
          return;
        }
        
        if (availableToClockIn.length === 1) {
          await api.clockIn({ ...locationData, shift_id: availableToClockIn[0].id });
          // Immediately update state to show Clock Out button
          setAttendance(prev => ({ ...prev, clocked_in: true }));
          await loadData();
        } else {
          setAvailableShifts(availableToClockIn);
          setPendingLocation(locationData);
          setShowShiftModal(true);
        }
      }
    } catch (error) {
      alert(error.message || 'Could not process clock action');
    } finally {
      setClockingIn(false);
    }
  };

  const handleShiftSelection = async (shiftId) => {
    setShowShiftModal(false);
    setClockingIn(true);
    try {
      await api.clockIn({ ...pendingLocation, shift_id: shiftId });
      // Immediately update state to show Clock Out button
      setAttendance(prev => ({ ...prev, clocked_in: true }));
      await loadData();
    } catch (error) {
      alert(error.message || 'Could not clock in');
    } finally {
      setClockingIn(false);
      setPendingLocation({});
      setAvailableShifts([]);
    }
  };

  if (loading) {
    return (
      <MobileLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-700" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout>
      <div className="p-4 space-y-6" data-testid="guard-dashboard">
        {/* Header */}
        <div className="flex justify-between items-start">
          <div>
            <p className="text-slate-500 text-sm">Hello,</p>
            <h1 className="text-2xl font-bold text-slate-800">{user?.name}</h1>
            <p className="text-blue-700 text-sm font-medium">
              {user?.role === 'super_admin' ? 'Super Admin' : 
               user?.role === 'admin' ? 'Admin' : 
               user?.role === 'supervisor' ? 'Supervisor' : 'Security Guard'}
            </p>
          </div>
          <div className="bg-white px-3 py-2 rounded-lg shadow-sm border">
            <p className="text-sm font-medium text-slate-700">{format(new Date(), 'EEE, MMM d')}</p>
          </div>
        </div>

        {/* Clock In/Out Card - Guards Only */}
        {!isSupervisor && (
          <Card className="border-slate-200">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className={`w-8 h-8 ${attendance?.clocked_in ? 'text-green-600' : 'text-slate-400'}`} />
                <div>
                  <p className="font-semibold text-slate-800">
                    {attendance?.clocked_in ? 'Currently On Duty' : 'Off Duty'}
                  </p>
                  {attendance?.attendance?.clock_in && (
                    <p className="text-xs text-slate-500">
                      Clocked in: {format(new Date(attendance.attendance.clock_in), 'h:mm a')}
                    </p>
                  )}
                </div>
              </div>
              <Button
                onClick={handleClockInOut}
                disabled={clockingIn}
                className={attendance?.clocked_in 
                  ? 'bg-red-600 hover:bg-red-700' 
                  : 'bg-green-600 hover:bg-green-700'}
                data-testid="clock-in-out-btn"
              >
                {clockingIn ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  attendance?.clocked_in ? 'Clock Out' : 'Clock In'
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Admin Panel Button - Supervisors */}
        {isSupervisor && (
          <button
            onClick={() => navigate('/admin')}
            className="w-full bg-blue-700 text-white rounded-xl p-4 flex items-center gap-4 hover:bg-blue-800 transition-colors"
            data-testid="admin-panel-btn"
          >
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Monitor className="w-6 h-6" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-bold text-lg">Admin Panel</p>
              <p className="text-blue-100 text-sm">Full dashboard, reports, schedules & more</p>
            </div>
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Platform Panel Button - Super Admin Only */}
        {user?.role === 'super_admin' && (
          <button
            onClick={() => navigate('/platform')}
            className="w-full bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-xl p-4 flex items-center gap-4 hover:from-violet-700 hover:to-purple-700 transition-all shadow-lg shadow-violet-500/25"
            data-testid="platform-panel-btn"
          >
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6" />
            </div>
            <div className="flex-1 text-left">
              <p className="font-bold text-lg">Platform Admin</p>
              <p className="text-violet-100 text-sm">Manage all organizations & billing</p>
            </div>
            <ChevronRight className="w-6 h-6" />
          </button>
        )}

        {/* Stats Grid */}
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Overview</h2>
          <div className="grid grid-cols-2 gap-3">
            {isSupervisor ? (
              <>
                <StatCard icon={Users} label="Total Guards" value={stats.total_guards || 0} color="blue" />
                <StatCard 
                  icon={MapPin} 
                  label="Active Today" 
                  value={stats.active_today || 0} 
                  color="green" 
                  onClick={() => navigate('/admin/tracking')}
                  clickable
                />
                <StatCard icon={QrCode} label="Checkpoints" value={stats.total_checkpoints || 0} color="sky" />
                <StatCard icon={QrCode} label="Scans Today" value={stats.scans_today || 0} color="indigo" />
                <StatCard 
                  icon={AlertTriangle} 
                  label="Open Incidents" 
                  value={stats.open_incidents || 0} 
                  color={stats.open_incidents > 0 ? 'red' : 'amber'}
                />
              </>
            ) : (
              <>
                <StatCard icon={QrCode} label="My Scans Today" value={stats.my_scans_today || 0} color="blue" />
                <StatCard icon={AlertTriangle} label="My Incidents" value={stats.my_incidents || 0} color="amber" />
              </>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Quick Actions</h2>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => navigate('/patrol')}
              className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-blue-300 transition-colors"
              data-testid="scan-checkpoint-btn"
            >
              <div className="w-12 h-12 bg-blue-50 rounded-full flex items-center justify-center">
                <QrCode className="w-6 h-6 text-blue-700" />
              </div>
              <span className="text-sm font-medium text-slate-700">Scan Checkpoint</span>
            </button>
            <button
              onClick={() => navigate('/incidents')}
              className="bg-white border border-slate-200 rounded-xl p-4 flex flex-col items-center gap-2 hover:border-amber-300 transition-colors"
              data-testid="report-incident-btn"
            >
              <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-amber-600" />
              </div>
              <span className="text-sm font-medium text-slate-700">Report Incident</span>
            </button>
          </div>
        </div>

        {/* Recent Scans */}
        {recentScans.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-800 mb-3">Recent Scans</h2>
            <Card>
              <CardContent className="p-0 divide-y divide-slate-100">
                {recentScans.map((scan) => (
                  <div key={scan.id} className="p-3 flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div className="flex-1">
                      <p className="font-medium text-slate-800 text-sm">{scan.checkpoint_name}</p>
                      <p className="text-xs text-slate-500">
                        {isSupervisor && `${scan.guard_name} • `}
                        {format(new Date(scan.scanned_at), 'h:mm a')}
                      </p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}
      </div>

      {/* Shift Selection Modal */}
      <Dialog open={showShiftModal} onOpenChange={setShowShiftModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="w-6 h-6 text-blue-700" />
              Select Your Shift
            </DialogTitle>
            <DialogDescription>
              You have multiple shifts scheduled today. Select one to clock in.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {availableShifts.map((shift) => (
              <button
                key={shift.id}
                onClick={() => handleShiftSelection(shift.id)}
                className="w-full p-4 bg-slate-50 hover:bg-slate-100 rounded-lg flex items-center justify-between transition-colors"
              >
                <div>
                  <p className="font-semibold text-slate-800">
                    {shift.start_time} - {shift.end_time}
                  </p>
                  {shift.site_name && (
                    <p className="text-sm text-blue-700 flex items-center gap-1 mt-1">
                      <MapPin className="w-4 h-4" />
                      {shift.site_name}
                    </p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
}

function StatCard({ icon: Icon, label, value, color, onClick, clickable }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700',
    green: 'bg-green-50 text-green-700',
    amber: 'bg-amber-50 text-amber-600',
    red: 'bg-red-50 text-red-600',
    sky: 'bg-sky-50 text-sky-700',
    indigo: 'bg-indigo-50 text-indigo-700',
  };

  const content = (
    <Card className={`${clickable ? 'cursor-pointer hover:border-blue-300 border-2 border-blue-200' : ''}`}>
      <CardContent className="p-4 flex flex-col items-center text-center">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
        <p className="text-2xl font-bold text-slate-800 mt-2">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
        {clickable && <p className="text-xs text-blue-700 mt-1 font-medium">Tap to track</p>}
      </CardContent>
    </Card>
  );

  if (onClick) {
    return <div onClick={onClick}>{content}</div>;
  }
  return content;
}